import React, { useEffect, useMemo, useState } from 'react';
import Bluebird from 'bluebird';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, generatePath, Link } from 'react-router-dom';
import { Box, Breadcrumbs, Button, Tooltip, Typography } from '@mui/material';
import { GroupAdd } from '@mui/icons-material';
import * as groupsActions from '../actions/groups';
import * as sitesActions from '../actions/sites';
import useActiveSite from '../hooks/useActiveSite';
import snackbarHelper from '../helpers/snackbarHelper';
import UsersTable from '../components/UsersTable';
import routes from '../routes';

const GroupAddUsersView = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { groupId } = useParams();
  const activeSite = useActiveSite();

  const groups = useSelector(state => state.entities.groups);
  const group = groups.byId[groupId];

  const siteUsers = useSelector(state => state.entities.sites.users);
  const allSiteUsers = (siteUsers.bySiteId && siteUsers.bySiteId[activeSite.id]) || [];

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(groupsActions.fetchSiteGroup(activeSite.id, groupId));
    dispatch(sitesActions.fetchSiteUsersIfNeeded(activeSite.id));
  }, [activeSite.id, groupId]);

  const nonMemberUsers = useMemo(() => {
    const memberIds = new Set((group?.users || []).map(u => u.id));
    return allSiteUsers.filter(u => !memberIds.has(u.id));
  }, [allSiteUsers, group?.users]);

  const handleAddToGroup = () => {
    if (!selectedUserIds.length) return;

    setSubmitting(true);
    Bluebird.try(() =>
      dispatch(groupsActions.addGroupUsers(activeSite.id, groupId, selectedUserIds))
    )
      .then(() => {
        snackbarHelper.success(`${selectedUserIds.length} user(s) added to group.`);
        history.push(generatePath(routes.allUsers, { tab: 'groups', subTab: groupId }));
      })
      .catch(err => snackbarHelper.error(err.message))
      .finally(() => setSubmitting(false));
  };

  const renderAddToGroupToolbar = () => (
    <Tooltip title={selectedUserIds.length ? 'Add selected users to group' : 'Select users to add'}>
      <span>
        <Button
          size="small"
          startIcon={<GroupAdd />}
          variant="contained"
          color="primary"
          onClick={handleAddToGroup}
          disabled={!selectedUserIds.length || submitting}
        >
          Add to Group
        </Button>
      </span>
    </Tooltip>
  );

  const groupMembersPath = generatePath(routes.allUsers, { tab: 'groups', subTab: groupId });

  return (
    <Box mb={12}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Typography color="text.secondary">Users</Typography>
        <Link
          to={generatePath(routes.allUsers, { tab: 'groups' })}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          Groups
        </Link>
        <Link to={groupMembersPath} style={{ textDecoration: 'none', color: 'inherit' }}>
          {group?.name || 'Group'}
        </Link>
        <Typography color="text.primary">Add Users</Typography>
      </Breadcrumbs>

      <UsersTable
        tableId={`#groups/add-users-${groupId}`}
        title={group ? `Add Users to "${group.name}"` : 'Add Users to Group'}
        users={nonMemberUsers}
        site={activeSite}
        customToolbar={renderAddToGroupToolbar}
        onRowSelectionChange={setSelectedUserIds}
        showSelectableRows
        showRolesColumn
        showVerifiedColumn={false}
        showEditUserButton={false}
        showDeleteUserButton={false}
        showDeleteSiteCoordinatorButton={false}
        showSentInvitationUserButton={false}
        showSendEmailButton={false}
        showSendSmsButton={false}
        showImpersonateUserButton={false}
        handleShowUsersWithAllSelectedRoles={() => {}}
      />
    </Box>
  );
};

export default GroupAddUsersView;
